import {Stack} from "@mui/material";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/de";
import "dayjs/locale/en-gb";
import { useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import SectionShell from "./AdminShared";
import BillingFilters from "./billing/BillingFilters";
import BillingOverview from "./billing/BillingOverview";
import BillingPriceSettings from "./billing/BillingPriceSettings";
import type {BillingGranularity} from "./AdminTypes";
import {getInitialDateRange} from "./billing/billingUtils";
import {useAdminBillingConfig, useAdminBillingInfo, useAdminLegalEntities} from "../../api/admin/adminQueries";

const BillingTab = () => {
    const {t: translate, i18n} = useTranslation();
    const dayjsLocale = i18n.resolvedLanguage?.toLowerCase().startsWith("de") ? "de" : "en-gb";

    const initialDateRange = useMemo(() => getInitialDateRange(), []);

    const [selectedLegalEntityId, setSelectedLegalEntityId] = useState("");
    const[selectedStartDate, setSelectedStartDate] = useState(initialDateRange.startDate);
    const [selectedEndDate, setSelectedEndDate] = useState(initialDateRange.endDate);
    const [selectedGranularity, setSelectedGranularity] = useState<BillingGranularity>("MONTHLY");

    const hasCompleteDateRange = selectedStartDate !== "" && selectedEndDate !== "";
    const hasValidDateRange = hasCompleteDateRange && selectedStartDate <= selectedEndDate;

    const legalEntitiesQuery = useAdminLegalEntities();
    const billingConfigQuery = useAdminBillingConfig();
    const billingQuery = useAdminBillingInfo(
        selectedLegalEntityId !== "" && hasValidDateRange ? selectedLegalEntityId : undefined,
        hasValidDateRange ? selectedStartDate : undefined,
        hasValidDateRange ? selectedEndDate : undefined,
        hasValidDateRange ? selectedGranularity : undefined,
    );

    const legalEntities = legalEntitiesQuery.data ?? [];
    const selectedLegalEntity = legalEntities.find((legalEntity) => legalEntity.id === selectedLegalEntityId) ?? null;

    const errorMessage =
        legalEntitiesQuery.error?.message || billingConfigQuery.error?.message || billingQuery.error?.message || "";

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
            <Stack spacing={3}>
                <SectionShell eyebrow={translate("admin.billing.eyebrow")} title={translate("admin.billing.title")}>
                    <Stack spacing={3}>
                        <BillingFilters
                            isLegalEntitiesLoading={legalEntitiesQuery.isLoading}
                            legalEntities={legalEntities}
                            selectedLegalEntityId={selectedLegalEntityId}
                            isLoading={billingQuery.isLoading}
                            selectedStartDate={""}
                            selectedEndDate={""}
                            selectedGranularity={""}
                            onSelectedLegalEntityChange={setSelectedLegalEntityId}
                            onStartDateChange={setSelectedStartDate}
                            onEndDateChange={setSelectedEndDate}
                            onGranularityChange={setSelectedGranularity}
                        />

                        <BillingOverview
                            billingInfo={billingQuery.data}
                            errorMessage={errorMessage}
                            isBillingLoading={billingQuery.isLoading}
                            selectedEndDate={selectedEndDate}
                            selectedGranularity={selectedGranularity}
                            selectedLegalEntity={selectedLegalEntity}
                            selectedStartDate={selectedStartDate}
                        />

                        <BillingPriceSettings
                            billingInfo={billingQuery.data}
                            billingConfig={billingConfigQuery.data}
                            billingConfigIsLoading={billingConfigQuery.isLoading}
                            selectedLegalEntity={selectedLegalEntity}
                        />
                    </Stack>
                </SectionShell>
            </Stack>
        </LocalizationProvider>
    );
};

export default BillingTab;
